/* eslint-disable no-undef */
import queryString from 'query-string';
import { redirectToLogout } from 'Utils/urlUtils';
import { VITE_AUTH_HEADER_PREFIX, VITE_GP_VERSION, VITE_DIMS_URL } from 'Constants/appEnvConstants';
import { loadState } from '../store/browserStorage.js';
import { openNotificationToast } from 'Utils/notificationMessageUtils.jsx';
import { pickBy } from 'Utils/lodashUtils.js';

export function configureHttpHeaders(axios) {
  /* eslint-disable no-param-reassign, dot-notation */
  const auth = loadState('auth');
  let accessToken = sessionStorage.getItem('__gp_auth_token__');
  if (accessToken) {
    accessToken = VITE_AUTH_HEADER_PREFIX + accessToken;
  }
  if (accessToken && accessToken.includes('undefined')) {
    accessToken = VITE_AUTH_HEADER_PREFIX + sessionStorage.getItem('__gp_auth_token__');
  }
  axios.defaults.headers.common['Accept'] = 'text/csv,application/pdf,application/json';
  axios.defaults.headers.common['Authorization'] = accessToken;
  axios.defaults.headers.common['x-gp-version'] = VITE_GP_VERSION;
  axios.defaults.headers.common['urlIdentifier'] = sessionStorage.getItem('appPortal');

  // Axios 1.6+ strips the Authorization header on cross-origin redirects (CVE-2023-45857 fix).
  // This interceptor re-attaches the token so MSAL token validation calls are not broken.
  axios.interceptors.request.use((config) => {
    const token = sessionStorage.getItem('__gp_auth_token__');
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = VITE_AUTH_HEADER_PREFIX + token;
    }
    return config;
  });
}

export function isLanguageSupported(langCode) {
  const supportedLanguages = ['en-US', 'en-GB', 'fr-CA', 'zh-Hans', 'zh-Hant', 'en-CA'];

  const langPosition = supportedLanguages.indexOf(langCode);

  return langPosition > -1;
}

export default {
  configureHttpHeaders,
  isLanguageSupported,
  combineUserProperties,
};

export function combineUserProperties(
  user,
  acquirer = {},
  role = {},
  permissions = [],
  organizationIds = [],
  primaryMerchant,
  parentUser,
) {
  const combinedUser = Object.assign({}, user);

  combinedUser.acquirerId = acquirer.acquirerId;

  combinedUser.role = {
    key: role.key,
    id: role.id,
    name: role.name,
    description: role.description,
    permissions: role.permissions,
    permissionIds: permissions.map((perm) => perm.permissionId),
  };

  if (Array.isArray(permissions)) {
    combinedUser.role.permissionIds = permissions
      .filter((perm) => {
        return perm.isChecked;
      })
      .map((perm) => {
        return perm.id;
      });
  } else {
    combinedUser.role.permissionIds = [];
  }
  if (!Array.isArray(combinedUser.organizationIds) || organizationIds.length > 0) {
    combinedUser.organizationIds = organizationIds;
  }

  if (primaryMerchant !== undefined) {
    combinedUser.primaryMerchant = primaryMerchant.merchantId;
  }

  if (parentUser !== undefined) {
    combinedUser.parentUserId = parentUser.userId;
  }
  return combinedUser;
}

export function getQueryStringParameterByName(name, url) {
  const urlToParse = url || window.location.href;
  const encodedName = name.replace(/[\[\]]/g, '\\$&'); // eslint-disable-line
  const regex = new RegExp(`[?&]${encodedName}(=([^&#]*)|&|#|$)`);

  const results = regex.exec(urlToParse);
  if (!results) return null;

  if (!results[2]) return '';

  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export function buildQueryStringFromObject(objectToStringify) {
  return queryString.stringify(objectToStringify);
}

export function filterRequestPayloadObject(objectToFilter, allowedKeys) {
  return pickBy(objectToFilter, (value, key) => {
    return allowedKeys.includes(key);
  });
}

export function filterRequestPayloadArray(arrayToFilter, allowedKeys) {
  return arrayToFilter.map((item) => {
    return filterRequestPayloadObject(item, allowedKeys);
  });
}

export function buildReportPreviewObject(report) {
  const columnIds = report.dataColumns?.map((column) => column.templateColumnId);
  const filters = report.filters
    .filter((previewFilter) => {
      return previewFilter.defaultValue && (previewFilter.defaultValue.v1 || previewFilter.defaultValue.v2);
    })
    .map((previewFilter) => {
      return {
        columnId: previewFilter.templateColumnId,
        value1: previewFilter.defaultValue.v1,
        value2: previewFilter.defaultValue.v2,
        type: previewFilter.defaultValue.type,
      };
    });

  return {
    columnIds,
    filters,
    templateId: report.templateId,
  };
}

export function onSuccessWithHeaders(response) {
  let result;

  if (response.status !== 204) {
    result = {
      status: response.status,
      data: response.data,
      headers: response.headers,
    };
  } else {
    result = 'Success';
  }
  return result;
}

export function onSuccess(response) {
  let result;

  if (response.status !== 204) {
    result = response.data ? response.data : (response = { data: 'Success' });
  } else {
    result = 'Success';
  }
  return result;
}

export function onError(error) {
  if (error && error.response) {
    if (error.response.status === 401) {
      redirectToLogout();
    }
    if (error.response.status === 403) {
      throw error;
    }
    if (error.response.status === 404) {
      throw error;
    }
    if (error.response.status >= 500 && error.response.status < 512) {
      const notificationMessageError = {
        type: 'error',
        placement: 'topRight',
        messageWidth: 250,
        byDefault: true,
        duration: 5,
        contentHtml: `<div style= "color: black;padding-top:10px !important;">
      <b>System Error</b>: Something went wrong.
      </div>`,
      };
      openNotificationToast(notificationMessageError);
    }
  } else {
    throw error;
  }
}

export function onErrorUnmatched(error) {
  if (error && error.response) {
    if (error.response.status === 401) {
      redirectToLogout();
    }
    if (error.response.status === 403) {
      throw error;
    }
    if (error.response.status === 404) {
      throw error;
    }
    if (error.response.status === 413) {
      return error.response.status;
    }
    if (error.response.status === 412) {
      return error.response.status;
    }
    if (error.response.status >= 500 && error.response.status < 512) {
      const notificationMessageError = {
        type: 'error',
        placement: 'topRight',
        messageWidth: 250,
        byDefault: true,
        duration: 5,
        contentHtml: `<div style= "color: black;padding-top:10px !important;">
      <b>System Error</b>: Something went wrong.
      </div>`,
      };
      openNotificationToast(notificationMessageError);
    }
    if (
      error.response.data !== undefined &&
      error.response.data.status !== undefined &&
      error.response.data.status.statusMsg !== undefined &&
      (error.response.data.status.statusMsg === 'FileSize Exceeded' ||
        error.response.data.status.statusMsg === 'queueName already exists' ||
        error.response.data.status.statusMsg === 'queueDeleted')
    ) {
      return error.response.data.status.statusMsg;
    }
  } else {
    throw error;
  }
}

export function onErrorUnresolveRTI(error) {
  if (error && error.response) {
    if (error.response.status === 401) {
      redirectToLogout();
    }
    if (error.response.status === 403) {
      throw error;
    }
    if (error.response.status === 404) {
      throw error;
    }
    if (error.response.status === 412) {
      return error.response.status;
    }
    if (error.response.status >= 500 && error.response.status < 512) {
      throw error;
    }
  } else {
    throw error;
  }
}
